export const PREDEFINED_TICKET_FIELD_KEYS = new Set([
  "accountNo",
  "mobileNumber",
  "agentName",
  "priority",
  "assignedTo",
  "targetOutstanding",
  "targetPaymentDate",
  "remarks",
  "callLogId",
  "status",
]);

/** Predefined columns stored in ticket customFields rather than top-level request fields. */
export const PREDEFINED_CUSTOM_FIELD_KEYS = new Set(["mobileNumber"]);

export const KEEP_AS_NEW_FIELD = "__keep_as_new__";

export const normalizeHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

export const resolveFieldKey = (header) => {
  const normalized = normalizeHeader(header);
  switch (normalized) {
    case "calllogid":
      return "callLogId";
    case "agentname":
    case "agent":
    case "assignee":
    case "assignedto":
    case "targetagent":
      return "agentName";
    case "accountno":
    case "accountid":
      return "accountNo";
    case "mobilenumber":
    case "mobile":
    case "mobileno":
    case "phonenumber":
    case "phone":
    case "customernumber":
    case "customerno":
    case "contactnumber":
    case "contactno":
      return "mobileNumber";
    case "status":
      return "status";
    case "priority":
      return "priority";
    case "remarks":
      return "remarks";
    case "outstanding":
    case "targetoutstanding":
    case "currentos":
      return "targetOutstanding";
    case "paymentdate":
    case "targetpaymentdate":
    case "lastpaymentdate":
      return "targetPaymentDate";
    default: {
      const parts = String(header || "")
        .trim()
        .split(/[\s_-]+/)
        .filter(Boolean);
      if (parts.length <= 1) {
        const single = parts[0] || "";
        return single ? single.charAt(0).toLowerCase() + single.slice(1) : "";
      }
      return parts
        .map((part, index) =>
          index === 0
            ? part.charAt(0).toLowerCase() + part.slice(1)
            : part.charAt(0).toUpperCase() + part.slice(1)
        )
        .join("");
    }
  }
};

export const getMissingMandatoryColumns = (headers = [], columnConfig = [], { uploadContext = false } = {}) => {
  const normalizedHeaders = new Set(headers.map((header) => resolveFieldKey(header)));
  return columnConfig
    .filter((column) => column.mandatory)
    .filter((column) => (uploadContext ? column.fieldKey !== "priority" : !column.uploadOnly))
    .filter((column) => (uploadContext ? true : column.fieldKey !== "agentName"))
    .filter((column) => !normalizedHeaders.has(column.fieldKey))
    .map((column) => column.displayLabel || column.fieldKey);
};

export const resolveMappedUploadFieldKey = (header, mappings = {}) => {
  const mapped = mappings?.[header];
  if (mapped && mapped !== KEEP_AS_NEW_FIELD) {
    return mapped;
  }
  return resolveFieldKey(header);
};

export const buildInitialUploadMappings = (headers = [], columnConfig = []) => {
  const availableFieldKeys = new Set((columnConfig || []).map((column) => column.fieldKey));
  return headers.reduce((acc, header) => {
    const resolved = resolveFieldKey(header);
    acc[header] = availableFieldKeys.has(resolved) ? resolved : KEEP_AS_NEW_FIELD;
    return acc;
  }, {});
};

export const getUploadMappingPayload = (headers = [], mappings = {}) =>
  headers.reduce((acc, header) => {
    const mapped = mappings?.[header];
    if (mapped && mapped !== KEEP_AS_NEW_FIELD) {
      acc[header] = mapped;
    }
    return acc;
  }, {});

export const getMissingMandatoryUploadColumns = (headers = [], columnConfig = [], mappings = {}) => {
  const targetFieldKeys = new Set(headers.map((header) => resolveMappedUploadFieldKey(header, mappings)));
  return columnConfig
    .filter((column) => column.mandatory)
    .filter((column) => column.fieldKey !== "priority")
    .filter((column) => !targetFieldKeys.has(column.fieldKey))
    .map((column) => column.displayLabel || column.fieldKey);
};

export const getDuplicateUploadMappings = (headers = [], mappings = {}) => {
  const headersByFieldKey = new Map();
  headers.forEach((header) => {
    const fieldKey = resolveMappedUploadFieldKey(header, mappings);
    if (!headersByFieldKey.has(fieldKey)) {
      headersByFieldKey.set(fieldKey, []);
    }
    headersByFieldKey.get(fieldKey).push(header);
  });
  return Array.from(headersByFieldKey.entries())
    .filter(([fieldKey, mappedHeaders]) => mappedHeaders.length > 1 && fieldKey !== "mobileNumber")
    .map(([fieldKey, mappedHeaders]) => ({
      fieldKey,
      headers: mappedHeaders,
    }));
};

export const getFormColumns = (columnConfig = []) =>
  columnConfig
    .filter((column) => column.visibleInForm && !column.uploadOnly)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

export const UPDATE_CORE_FIELD_KEYS = new Set([
  "status",
  "remarks",
  "targetOutstanding",
  "targetPaymentDate",
]);

export const FILTER_CORE_FIELD_KEYS = new Set([
  "accountNo",
  "status",
  "targetOutstanding",
  "targetPaymentDate",
]);

export const FILTER_ENTITY_FIELD_KEYS = new Set(["priority", "remarks"]);

export const FILTER_SYSTEM_FIELD_KEYS = new Set(["assignedTo", "agentName", "callLogId"]);

export const getFilterColumns = (columnConfig = []) =>
  columnConfig
    .filter((column) => column.visibleInFilter && !column.uploadOnly)
    .filter((column) => !FILTER_CORE_FIELD_KEYS.has(column.fieldKey))
    .filter((column) => !FILTER_SYSTEM_FIELD_KEYS.has(column.fieldKey))
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

export const isColumnVisibleInFilter = (columnConfig = [], fieldKey) => {
  const column = columnConfig.find((item) => item.fieldKey === fieldKey);
  if (!column) {
    return FILTER_CORE_FIELD_KEYS.has(fieldKey);
  }
  return Boolean(column.visibleInFilter);
};

export const buildTicketListFilterParams = (filters = {}) => {
  const params = {};
  const customFields = filters.customFields || {};

  Object.entries(customFields).forEach(([fieldKey, value]) => {
    if (value == null || String(value).trim() === "") {
      return;
    }
    params[`customField.${fieldKey}`] = String(value).trim();
  });

  if (filters.priority) {
    params.priority = String(filters.priority).trim();
  }
  if (filters.remarks) {
    params.remarks = String(filters.remarks).trim();
  }

  return params;
};

// Dynamic (custom, non-predefined) fields are always editable in the update form so
// agents can update any dynamic field; predefined extras still respect their toggle.
const isDynamicUpdatableColumn = (column) =>
  Boolean(column) && !column.uploadOnly && (column.visibleInUpdate || column.predefined === false);

export const getUpdateColumns = (columnConfig = []) =>
  columnConfig
    .filter((column) => isDynamicUpdatableColumn(column))
    .filter((column) => !UPDATE_CORE_FIELD_KEYS.has(column.fieldKey))
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

export const isColumnVisibleInUpdate = (columnConfig = [], fieldKey) => {
  const column = columnConfig.find((item) => item.fieldKey === fieldKey);
  if (!column) return true;
  if (column.predefined === false && !column.uploadOnly) return true;
  return Boolean(column.visibleInUpdate);
};

export const buildTicketUpdateCustomFieldsDraft = (ticket, updateColumns = []) =>
  updateColumns.reduce((acc, column) => {
    const rawValue = getTicketFieldValue(ticket, column.fieldKey, "");
    acc[column.fieldKey] = rawValue == null || rawValue === "—" ? "" : String(rawValue);
    return acc;
  }, {});

export const getListColumns = (columnConfig = []) =>
  columnConfig
    .filter((column) => column.visibleInList)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

export const getTicketFieldValue = (ticket, fieldKey, fallback = "—") => {
  if (!ticket) return fallback;
  switch (fieldKey) {
    case "accountNo":
      return ticket.accountNo ?? ticket.accountId ?? fallback;
    case "mobileNumber":
      return ticket.customFields?.mobileNumber ?? ticket.mobileNumber ?? fallback;
    case "status":
      return ticket.status ?? fallback;
    case "priority":
      return ticket.priority ?? fallback;
    case "targetOutstanding":
      return ticket.targetOutstanding ?? fallback;
    case "targetPaymentDate":
      return ticket.targetPaymentDate ?? fallback;
    case "remarks":
      return ticket.remarksComment || ticket.remarks || fallback;
    case "assignedTo":
      return ticket.assignedTo ?? fallback;
    default:
      return ticket.customFields?.[fieldKey] ?? fallback;
  }
};

export const parseCsvHeaders = (text) => {
  const firstLine = String(text || "").split(/\r?\n/)[0] || "";
  return firstLine
    .split(",")
    .map((item) => item.replace(/^"|"$/g, "").trim())
    .filter(Boolean);
};

export const readUploadHeaders = async (file) => {
  const fileName = file?.name?.toLowerCase() || "";
  if (fileName.endsWith(".csv")) {
    const text = await file.text();
    return parseCsvHeaders(text);
  }

  const XLSX = await import("xlsx");
  const fileBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(fileBuffer, { type: "array" });
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
  return Array.isArray(rows?.[0]) ? rows[0].filter(Boolean) : [];
};
